import { useEffect } from 'react';
import {
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Switch,
  RadioGroup,
  RadioGroupItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { RecordingConfig, SnapshotConfig, StorageConfig, EventJobConfig } from '@/api/media/live-event-jobs-types';
import { FormSectionCard } from './FormSectionCard';
import styles from './EventJobCreate.module.css';

// Legacy contract: snapshot storage type is always 'swiftserve_cdn'
type RecordingStorageType = 'swiftserve_cdn' | 'ftp' | 'sftp' | 's3';

const RECORDING_STORAGE_TYPES: { value: RecordingStorageType; label: string }[] = [
  { value: 'swiftserve_cdn', label: 'CDN Endpoint' },
  { value: 'ftp', label: 'FTP' },
  { value: 'sftp', label: 'SFTP' },
  { value: 's3', label: 'S3' },
];

// Snapshot storage: swiftserve_cdn ONLY (legacy contract).
const SNAPSHOT_STORAGE_TYPES: { value: RecordingStorageType; label: string }[] = [
  { value: 'swiftserve_cdn', label: 'CDN Endpoint' },
];

// Forced snapshot path — not editable (legacy :246-247)
const SNAPSHOT_PATH = '/pub/__defaultVhost__/{app_name}/{stream_name}/{yyyyMMdd}/';

function getStorageList(config: EventJobConfig | undefined, type: RecordingStorageType) {
  if (!config) return [];
  if (type === 'swiftserve_cdn') return config.cdns ?? [];
  if (type === 's3') return config.output_storages?.s3 ?? [];
  if (type === 'ftp') return config.output_storages?.ftp ?? [];
  if (type === 'sftp') return config.output_storages?.sftp ?? [];
  return [];
}

interface StorageSelectorProps {
  value: StorageConfig;
  availableTypes: { value: RecordingStorageType; label: string }[];
  config: EventJobConfig | undefined;
  /** When set, path field is read-only and shows this value */
  forcedPath?: string;
  onChange: (next: StorageConfig) => void;
}

function StorageSelector({ value, availableTypes, config, forcedPath, onChange }: StorageSelectorProps) {
  const currentType = value.type as RecordingStorageType;
  const items = getStorageList(config, currentType);

  // Auto-select first available item when type changes or config loads
  useEffect(() => {
    if (items.length === 0) return;
    const ids = items.map((i) => String((i as { id: number }).id));
    if (!value.storage_id || !ids.includes(value.storage_id)) {
      onChange({ ...value, storage_id: String((items[0] as { id: number }).id) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentType, items.length]);

  return (
    <div className={styles.storageBlock}>
      <Field label="Storage Type" required>
        {(fp) => (
          <Select
            value={currentType}
            // Preserve path across type switches (legacy :97 keeps path, only resets storage_id)
            onValueChange={(t) => onChange({ type: t, storage_id: '', path: forcedPath ?? value.path })}
          >
            <SelectTrigger {...fp}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>

      <Field label="Storage" required>
        {(fp) => (
          <Select
            value={value.storage_id || undefined}
            onValueChange={(v) => onChange({ ...value, storage_id: v })}
          >
            <SelectTrigger {...fp}>
              <SelectValue placeholder="Select storage" />
            </SelectTrigger>
            <SelectContent>
              {items.map((item) => {
                const it = item as { id: number; name: string };
                return <SelectItem key={it.id} value={String(it.id)}>{it.name}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        )}
      </Field>

      {forcedPath ? (
        <Field label="Path">
          {(fp) => (
            <Input
              {...fp}
              className={styles.inputReadonly}
              value={forcedPath}
              readOnly
              aria-label="Snapshot path (fixed)"
            />
          )}
        </Field>
      ) : (
        <Field label="Path">
          {(fp) => (
            <Input
              {...fp}
              value={value.path}
              onChange={(e) => onChange({ ...value, path: e.target.value })}
              placeholder="/recordings"
            />
          )}
        </Field>
      )}
    </div>
  );
}

const DEFAULT_RECORDING_MODES: { value: RecordingConfig['mode']; label: string }[] = [
  { value: 'hls', label: 'HLS' },
  { value: 'mp4', label: 'MP4' },
];

interface RecordingSnapshotSectionProps {
  recording: RecordingConfig;
  snapshot: SnapshotConfig;
  config: EventJobConfig | undefined;
  onRecording: (patch: Partial<RecordingConfig>) => void;
  onSnapshot: (patch: Partial<SnapshotConfig>) => void;
  /**
   * Override the default HLS/MP4 mode list (e.g. linear wizard passes
   * `[{ value: 'dash', label: 'DASH' }]` when protocol forces DASH recording).
   */
  recordingModes?: { value: RecordingConfig['mode']; label: string }[];
}

/** Recording (HLS/MP4 + storage) and snapshot (JPG + CDN-only storage with fixed path) toggles. */
export function RecordingSnapshotSection({
  recording, snapshot, config, onRecording, onSnapshot, recordingModes,
}: RecordingSnapshotSectionProps) {
  const effectiveModes = recordingModes ?? DEFAULT_RECORDING_MODES;

  // Seed default storage object when enabling recording/snapshot so payload is never storage-empty
  // ftp is the legacy default type (RecordingSnapshotSection.tsx:147)
  const defaultRecordingStorage: StorageConfig = { type: 'ftp', storage_id: '', path: '' };
  const defaultSnapshotStorage: StorageConfig = { type: 'swiftserve_cdn', storage_id: '', path: SNAPSHOT_PATH };

  // When the caller overrides recordingModes (e.g. protocol changes), reset to the first available mode.
  // This keeps both the sent payload AND the UI radio in sync (MINOR 17).
  const validModes = effectiveModes.map((m) => m.value);
  const currentMode: RecordingConfig['mode'] =
    recording.mode && validModes.includes(recording.mode)
      ? recording.mode
      : (effectiveModes[0]?.value ?? 'mp4');

  useEffect(() => {
    if (!recording.enabled) return;
    if (recording.mode && !validModes.includes(recording.mode)) {
      onRecording({ mode: effectiveModes[0]?.value });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingModes]);

  const handleRecordingToggle = (enabled: boolean) => {
    onRecording({
      enabled,
      mode: currentMode,
      storage: recording.storage ?? defaultRecordingStorage,
    });
  };

  const handleSnapshotToggle = (enabled: boolean) => {
    onSnapshot({
      enabled,
      type: 'jpg',
      storage: snapshot.storage ?? defaultSnapshotStorage,
    });
  };

  return (
    <>
      {/* Recording — whole section gated by the header Switch */}
      <FormSectionCard
        title="Recording"
        description="Optionally record the stream to storage."
        actions={
          <label className={styles.switchItem} htmlFor="rec-toggle">
            <span>Enable Recording</span>
            <Switch checked={recording.enabled} onCheckedChange={handleRecordingToggle} id="rec-toggle" />
          </label>
        }
      >
        {recording.enabled && (
          <>
            <fieldset className={styles.fieldsetReset}>
              <legend className={styles.fieldLegend}>Format</legend>
              <RadioGroup
                value={currentMode}
                onValueChange={(v) => onRecording({ mode: v as RecordingConfig['mode'] })}
                className={styles.radioGroup}
              >
                {effectiveModes.map(({ value: m, label }) => (
                  <label key={m} className={styles.radioItem}>
                    <RadioGroupItem value={m} id={`rec-mode-${m}`} />
                    <span>{label}</span>
                  </label>
                ))}
              </RadioGroup>
            </fieldset>

            <StorageSelector
              value={recording.storage ?? defaultRecordingStorage}
              availableTypes={RECORDING_STORAGE_TYPES}
              config={config}
              onChange={(s) => onRecording({ storage: s })}
            />
          </>
        )}
      </FormSectionCard>

      {/* Snapshot — storage type forced to swiftserve_cdn, path non-editable */}
      <FormSectionCard
        title="Snapshot"
        description="Optionally capture JPG snapshots to CDN storage."
        actions={
          <label className={styles.switchItem} htmlFor="snap-toggle">
            <span>Enable Snapshot (JPG)</span>
            <Switch checked={snapshot.enabled} onCheckedChange={handleSnapshotToggle} id="snap-toggle" />
          </label>
        }
      >
        {snapshot.enabled && (
          <StorageSelector
            value={snapshot.storage ?? defaultSnapshotStorage}
            availableTypes={SNAPSHOT_STORAGE_TYPES}
            config={config}
            forcedPath={SNAPSHOT_PATH}
            onChange={(s) => onSnapshot({ storage: { ...s, path: SNAPSHOT_PATH } })}
          />
        )}
      </FormSectionCard>
    </>
  );
}
